import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Coffee, Truck, Home, Film, Heart, ShoppingBag, DollarSign, Smartphone,
  Zap, Book, Briefcase, Target, Tool, Music, Folder,
  Clock, Bookmark, AlertTriangle, X, RefreshCw, Umbrella, Gift,
  Star, Flag, Sun, Send, Camera, Compass, ChevronDown, ChevronUp
} from 'react-feather';
import { apiClient } from '../shared/api/client';
import { useMonth } from '../contexts/MonthContext';
import { Transaction } from '../shared/types';
import { parseAmount } from '../shared/utils/amount';
import PageHeader from '../components/PageHeader';
import CategorizeModal from '../components/CategorizeModal';
import WalletModal from '../components/WalletModal';
import MonthCheckup from '../components/MonthCheckup';

interface BudgetCategory {
  id: number;
  categoryId: number;
  categoryName: string;
  categoryIcon?: string;
  categoryColor?: string;
  budget: number;
  standardBudget: number;
  spent: number;
  remaining: number;
  percentage: number;
  isOverBudget: boolean;
  upcoming: number;
  upcomingNames: string[];
  earmarked: number;
  earmarkItems: { name: string; monthlyShare: number; fullAmount: number; saved: number; dueYear: number; dueMonth: number; accountId?: number | null; accountName?: string | null }[];
  hasBudget: boolean;
  categoryIds: number[];
  childBreakdown: { id: number; name: string; color?: string; spent: number }[];
}

// The month view: one verdict at the top (free to spend, with the normal
// monthly headroom as reference), then the category bars with their
// twelve-month average marked on each bar. Caps are editable per month.
const BudgetPage: React.FC = () => {
  const { currentMonth } = useMonth();
  const navigate = useNavigate();
  const [budgetData, setBudgetData] = useState<BudgetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategoryId, setExpandedCategoryId] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [upcomingItems, setUpcomingItems] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<any | null>(null);
  const [editingCapId, setEditingCapId] = useState<number | null>(null);
  const [capMonthValue, setCapMonthValue] = useState('');
  const [capStandardValue, setCapStandardValue] = useState('');
  const [savingCap, setSavingCap] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<{ id: number; account_name: string; account_type: string; balance: number; last_sync_at?: string; consent_status?: string; consent_expires?: string }[]>([]);

  const [syncing, setSyncing] = useState(false);
  const [categorizeTx, setCategorizeTx] = useState<Transaction | null>(null);
  // Cash verdict: next salary and the bills that hit before it
  const [nextSalary, setNextSalary] = useState<{ date: string; amount: number } | null>(null);
  const [preSalaryExpenses, setPreSalaryExpenses] = useState(0);
  const [preSalaryItems, setPreSalaryItems] = useState<any[]>([]);
  const [walletOpen, setWalletOpen] = useState(false);
  // Failures the user must see - a silent console.error looks like success
  const [actionError, setActionError] = useState<string | null>(null);
  // Savings goals (kassor): categories measured against saved-up money
  // instead of a monthly cap
  const [goals, setGoals] = useState<any[]>([]);
  // The start view answers one question with one number. Everything else -
  // bars, wallet, check-up, goals - lives behind "Visa detaljer", because a
  // wall of numbers on open is what made the app stressful to look at.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [checkupCount, setCheckupCount] = useState(0);

  const loadBankAccounts = () => {
    apiClient.get('/bank/accounts')
      .then(response => setBankAccounts((response as any).accounts || []))
      .catch(err => console.error('Failed to load bank accounts:', err));
  };

  // Real account balances - the verifiable anchor against the bank
  useEffect(() => {
    loadBankAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // User-triggered sync: attended access, allowed beyond the nightly quota
  const handleSyncNow = async () => {
    if (syncing) return;
    try {
      setSyncing(true);
      await apiClient.post('/bank/sync-now');
      await Promise.all([loadBudgets(), loadTransactions()]);
      loadBankAccounts();
    } catch (err) {
      console.error('Sync now failed:', err);
      setActionError('Kunde inte hämta från banken just nu. Banken begränsar antalet hämtningar - prova om en stund.');
    } finally {
      setSyncing(false);
    }
  };

  // Normal monthly picture (averages over complete months) - the reference
  // level shown in the verdict and as markers on the bars
  useEffect(() => {
    apiClient.get('/stats/categories?months=12')
      .then(response => setStats(response as any))
      .catch(err => console.error('Failed to load stats:', err));
  }, []);

  const isFutureMonth = () => {
    const now = new Date();
    const current = new Date(now.getFullYear(), now.getMonth(), 1);
    const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return selected > current;
  };

  const isCurrentMonth = () => {
    const now = new Date();
    return currentMonth.getFullYear() === now.getFullYear() && currentMonth.getMonth() === now.getMonth();
  };

  // Find the next salary (largest expected income next month) and sum the
  // charges expected to hit BEFORE it - the horizon for "money to live on"
  useEffect(() => {
    if (!isCurrentMonth()) { setNextSalary(null); setPreSalaryExpenses(0); setPreSalaryItems([]); return; }
    // The horizon is the next salary to ARRIVE. At the start of a month that
    // is this month's salary - looking only at next month counted nearly two
    // months of charges against today's balance.
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    Promise.all([
      apiClient.get(`/forecast?year=${currentMonth.getFullYear()}&month=${currentMonth.getMonth() + 1}`),
      apiClient.get(`/forecast?year=${next.getFullYear()}&month=${next.getMonth() + 1}`),
    ])
      .then(([thisResp, nextResp]) => {
        const thisItems = ((thisResp as any).forecast || []) as any[];
        const nextItems = ((nextResp as any).forecast || []) as any[];
        const incomes = [...thisItems, ...nextItems]
          .filter(i => i.amount > 0 && i.date >= todayStr)
          .sort((a, b) => a.date.localeCompare(b.date));
        const salary = incomes.find(i => i.amount > 10000) || incomes[0] || null;
        setNextSalary(salary ? { date: salary.date, amount: salary.amount } : null);
        const cutoff = salary ? salary.date : null;
        // Only what falls between now and that salary counts as claimed
        const preSalary = nextItems.filter(i => i.amount < 0 && (!cutoff || i.date <= cutoff));
        setPreSalaryExpenses(preSalary.reduce((sum, i) => sum + Math.abs(i.amount), 0));
        setPreSalaryItems(preSalary);
      })
      .catch(() => { setNextSalary(null); setPreSalaryExpenses(0); setPreSalaryItems([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const loadBudgets = async () => {
    try {
      // Only show the full-page loader on first load - background refreshes
      // (e.g. after linking a refund in the modal) must not unmount the UI
      if (budgetData.length === 0) setLoading(true);
      setError(null);
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await apiClient.get(`/budgets?year=${year}&month=${month}`);
      const budgetsArray = (response as any).budgets || [];
      setBudgetData(budgetsArray.map((budget: any) => ({
        id: budget.id,
        categoryId: budget.categoryId,
        categoryName: budget.categoryName,
        categoryIcon: budget.categoryIcon,
        categoryColor: budget.categoryColor || '#8E8E93',
        budget: budget.budget,
        standardBudget: budget.standardBudget || 0,
        spent: budget.spent,
        remaining: budget.remaining,
        percentage: budget.percentage,
        isOverBudget: budget.isOverBudget,
        upcoming: budget.upcoming || 0,
        upcomingNames: budget.upcomingNames || [],
        earmarked: budget.earmarked || 0,
        earmarkItems: budget.earmarkItems || [],
        hasBudget: budget.hasBudget !== false,
        categoryIds: budget.categoryIds || [budget.categoryId],
        childBreakdown: budget.childBreakdown || []
      })));
    } catch (err) {
      console.error('Load budgets error:', err);
      setError('Kunde inte ansluta till servern. Kontrollera din internetanslutning och försök igen.');
      setBudgetData([]);
    } finally {
      setLoading(false);
    }
  };

  // Actual transactions this month (for the expanded card lists) and, for
  // the current month, the still-expected charges
  const loadTransactions = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;

      if (isFutureMonth()) {
        const response = await apiClient.get(`/forecast?year=${year}&month=${month}`);
        const forecastArray = (response as any).forecast || [];
        setTransactions(forecastArray.map((tx: any) => ({ ...tx, isPredicted: true })));
        setUpcomingItems([]);
        return;
      }

      // Ask the server for exactly this month - fetching "the latest 500" and
      // filtering here silently drops older months once history grows
      const response = await apiClient.get(`/transactions?year=${year}&month=${month}&limit=500`);
      setTransactions((response as any).transactions || []);

      const now = new Date();
      if (year === now.getFullYear() && month === now.getMonth() + 1) {
        try {
          const fcResponse = await apiClient.get(`/forecast?year=${year}&month=${month}`);
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          // A recurring charge stays listed as expected for the whole month
          // until its transaction actually books - the day is only a hint.
          // Planned one-offs keep the date filter.
          const items = ((fcResponse as any).forecast || [])
            .filter((item: any) => item.source === 'rule' ? !item.alreadyBooked : item.date > todayStr)
            .map((item: any) => ({ ...item, isPredicted: true }));
          setUpcomingItems(items);
        } catch {
          setUpcomingItems([]);
        }
      } else {
        setUpcomingItems([]);
      }
    } catch (err) {
      console.error('Load transactions error:', err);
      setTransactions([]);
    }
  };

  useEffect(() => {
    loadBudgets();
    loadTransactions();
    apiClient.get('/goals')
      .then(response => setGoals((response as any).goals || []))
      .catch(err => console.error('Failed to load goals:', err));
    // The count is needed on the calm view, before the details (and the
    // check-up component itself) have ever been opened
    apiClient.get('/checkup')
      .then(response => setCheckupCount((((response as any).items || []) as any[]).length))
      .catch(() => setCheckupCount(0));
    setExpandedCategoryId(null);
    setEditingCapId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const getIconComponent = (iconName?: string) => {
    const iconMap: { [key: string]: any } = {
      'folder': Folder, 'coffee': Coffee, 'truck': Truck, 'home': Home,
      'film': Film, 'heart': Heart, 'shopping-bag': ShoppingBag,
      'dollar-sign': DollarSign, 'smartphone': Smartphone, 'zap': Zap,
      'book': Book, 'briefcase': Briefcase, 'target': Target, 'tool': Tool,
      'music': Music
    };
    return iconMap[iconName || 'folder'] || Folder;
  };

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount) + ' kr';

  const monthNameOf = (month: number, year: number) => {
    const names = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
    const label = names[month - 1] || '';
    return year !== new Date().getFullYear() ? `${label} ${year}` : label;
  };

  // Six-month average for a top-level category - the reference marker
  const avgFor = (categoryId: number): number => {
    const bucket = (stats?.tree || []).find((b: any) => b.id === categoryId);
    return bucket ? bucket.monthlyAverage : 0;
  };

  const getBudgetStatus = (spent: number, budget: number, upcoming: number = 0, earmarked: number = 0) => {
    const remaining = budget - spent;
    const freeAmount = budget - spent - upcoming - earmarked;
    const percentage = budget > 0 ? Math.min((spent / budget) * 100, 100) : 100;
    const upcomingPercentage = budget > 0 ? Math.min(((upcoming + earmarked) / budget) * 100, 100 - percentage) : 0;
    // Color reflects spent + expected upcoming + earmarked reserves, so a
    // bar with charges on the way warns before the money is actually gone
    const committedShare = budget > 0 ? ((spent + upcoming + earmarked) / budget) * 100 : 0;

    let color = '#34C759';
    if (freeAmount < 0) {
      color = '#FF3B30';
    } else if (committedShare >= 90) {
      color = '#FF9500';
    } else if (committedShare >= 75) {
      color = '#FFCC00';
    }

    return { remaining, freeAmount, percentage, upcomingPercentage, color };
  };

  const startCapEdit = (category: BudgetCategory) => {
    setEditingCapId(category.categoryId);
    setCapMonthValue(category.hasBudget ? String(category.budget) : '');
    setCapStandardValue(category.standardBudget > 0 ? String(category.standardBudget) : '');
  };

  // Save the cap editor: the month field is a one-month override, the
  // standard field seeds this and future months' rollovers - only the
  // fields that actually changed are saved
  const saveCap = async (category: BudgetCategory) => {
    if (savingCap) return;
    const parse = (v: string) => parseAmount(v) ?? NaN;
    const monthAmount = parse(capMonthValue);
    const standardAmount = parse(capStandardValue);
    const monthChanged = !isNaN(monthAmount) && monthAmount >= 0 && monthAmount !== category.budget;
    const standardChanged = !isNaN(standardAmount) && standardAmount >= 0 && standardAmount !== category.standardBudget;
    if (!monthChanged && !standardChanged) { setEditingCapId(null); return; }
    try {
      setSavingCap(true);
      const base = {
        categoryId: category.categoryId,
        year: currentMonth.getFullYear(),
        month: currentMonth.getMonth() + 1,
      };
      if (monthChanged) {
        await apiClient.post('/budgets', { ...base, amount: monthAmount });
      }
      if (standardChanged) {
        await apiClient.post('/budgets', { ...base, amount: standardAmount, scope: 'standard' });
      }
      setEditingCapId(null);
      await loadBudgets();
    } catch (err) {
      console.error('Failed to save cap:', err);
      setActionError('Taket kunde inte sparas. Försök igen.');
    } finally {
      setSavingCap(false);
    }
  };

  const renderBudgetBar = (category: BudgetCategory) => {
    const { freeAmount, percentage, upcomingPercentage, color } = getBudgetStatus(category.spent, category.budget, category.upcoming, category.earmarked);
    const IconComponent = getIconComponent(category.categoryIcon);
    const isExpanded = expandedCategoryId === category.categoryId;
    const avg = avgFor(category.categoryId);
    const avgPct = category.hasBudget && category.budget > 0 && avg > 0
      ? Math.min((avg / category.budget) * 100, 99.5)
      : null;

    // Details for the expanded view: actual transactions this month plus
    // expected upcoming charges (predicted list for future months)
    const familyIds = category.categoryIds || [category.categoryId];
    const actuals = isFutureMonth()
      ? []
      : transactions.filter(tx => !tx.isPredicted && tx.category && familyIds.includes(tx.category.id) && !tx.isTransfer);
    const upcoming = isFutureMonth()
      ? transactions.filter(tx => tx.isPredicted && tx.amount < 0 && tx.category && familyIds.includes(tx.category.id))
      : upcomingItems.filter(tx => tx.amount < 0 && tx.category && familyIds.includes(tx.category.id));

    return (
      <div
        key={category.id || `cat-${category.categoryId}`}
        className="budget-category-card bg-white rounded-xl p-4 mb-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setExpandedCategoryId(isExpanded ? null : category.categoryId)}
      >
        <div className="category-header flex justify-between items-center mb-2">
          <h3 className="category-title text-base font-semibold text-gray-900 flex-1 flex items-center">
            <div className="w-6 h-6 flex items-center justify-center mr-2 text-gray-900">
              <IconComponent size={16} />
            </div>
            {category.categoryName}
          </h3>
          <span className="relative" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => editingCapId === category.categoryId ? setEditingCapId(null) : startCapEdit(category)}
              className="budget-total text-base text-gray-500 hover:text-blue-600 hover:underline decoration-dotted underline-offset-4"
              title="Ändra tak"
            >
              {category.hasBudget ? formatAmount(category.budget) : 'sätt tak'}
            </button>
            {editingCapId === category.categoryId && (
              <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-60 cursor-default">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-900">Tak för {category.categoryName}</p>
                  <button onClick={() => setEditingCapId(null)} className="p-0.5 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
                </div>
                <label className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm text-gray-600">{monthNameOf(currentMonth.getMonth() + 1, currentMonth.getFullYear()).charAt(0).toUpperCase() + monthNameOf(currentMonth.getMonth() + 1, currentMonth.getFullYear()).slice(1)}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={capMonthValue}
                    onChange={e => setCapMonthValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveCap(category); if (e.key === 'Escape') setEditingCapId(null); }}
                    className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right"
                    autoFocus
                  />
                </label>
                <label className="flex items-center justify-between gap-2 mb-3">
                  <span className="text-sm text-gray-600">Standard</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={capStandardValue}
                    onChange={e => setCapStandardValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveCap(category); if (e.key === 'Escape') setEditingCapId(null); }}
                    className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right"
                  />
                </label>
                <p className="text-[11px] text-gray-400 mb-3 leading-snug">
                  Månadens tak gäller bara {monthNameOf(currentMonth.getMonth() + 1, currentMonth.getFullYear())}. Standard gäller kommande månader.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingCapId(null)}
                    className="flex-1 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={() => saveCap(category)}
                    disabled={savingCap}
                    className="flex-1 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                  >
                    {savingCap ? 'Sparar…' : 'Spara'}
                  </button>
                </div>
              </div>
            )}
          </span>
        </div>

        <div className={`flex items-center gap-3 ${avgPct !== null ? 'mt-4' : ''}`}>
          <div className="progress-bar-wrapper relative flex-1">
            {/* "snitt"-label anchored right at the average marker line */}
            {avgPct !== null && (
              <>
                <span
                  className={`absolute -top-[15px] text-[10px] text-gray-500 leading-none whitespace-nowrap ${avgPct > 82 ? '-translate-x-full' : avgPct < 10 ? '' : '-translate-x-1/2'}`}
                  style={{ left: `${avgPct}%` }}
                >
                  snitt {formatAmount(avg)}
                </span>
                <span
                  className="absolute -top-[5px] h-[5px] w-[2px] bg-gray-900 opacity-40"
                  style={{ left: `${avgPct}%` }}
                ></span>
              </>
            )}
            <div
              className="progress-bar-container h-6 rounded-full overflow-hidden relative"
              style={{ backgroundColor: category.hasBudget ? color : '#E5E7EB' }}
            >
              {/* Expected upcoming charges - striped layer that starts at 0
                  and runs BEHIND the spent bar, so the pattern continues
                  seamlessly out from under it */}
              {upcomingPercentage > 0 && (
                <div
                  className="absolute top-0 left-0 h-full rounded-full"
                  style={{
                    width: `${Math.min(percentage, 100) + upcomingPercentage}%`,
                    backgroundImage: 'repeating-linear-gradient(45deg, rgba(229,231,235,0.92) 0 8px, rgba(229,231,235,0.45) 8px 12px)'
                  }}
                  title={`${formatAmount(category.upcoming + category.earmarked)} väntas dras eller öronmärkt`}
                ></div>
              )}
              <div
                className="progress-bar-fill relative h-full bg-gray-200 rounded-full transition-all duration-300 flex items-center justify-end"
                style={{ width: `${Math.min(percentage, 100)}%` }}
              >
                {percentage > 5 && (
                  <span className="spent-amount text-xs font-semibold text-gray-500 pr-2">
                    {formatAmount(category.spent)}
                  </span>
                )}
              </div>
              {/* Six-month average marker: context instead of alarm - "over
                  the cap but at your normal level" reads at a glance */}
              {avgPct !== null && (
                <div
                  className="absolute top-0 bottom-0 w-[2px] bg-gray-900 opacity-40"
                  style={{ left: `${avgPct}%` }}
                  title={`Snitt ${formatAmount(avg)}/mån (12 mån)`}
                ></div>
              )}
            </div>
          </div>
          {category.hasBudget ? (
            <span className={`amount-remaining text-base font-semibold ${freeAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatAmount(Math.abs(freeAmount))}
              {freeAmount >= 0 ? ' fritt' : ' över'}
            </span>
          ) : (
            <span className="amount-remaining text-sm font-medium text-gray-500">
              inget tak satt
            </span>
          )}
        </div>
        {category.upcoming > 0 && (
          <p className="text-xs text-gray-500 mt-1 text-right flex items-center justify-end">
            <Clock size={12} className="mr-1" />
            {formatAmount(category.upcoming)} väntas dras
            {category.upcomingNames.length > 0 && ` (${category.upcomingNames.join(', ')})`}
          </p>
        )}
        {(category.earmarkItems || []).map((em, idx) => (
          <p key={idx} className="text-xs text-gray-500 mt-1 text-right flex items-center justify-end">
            <Bookmark size={12} className="mr-1" />
            {formatAmount(em.monthlyShare)}/mån öronmärkt för {em.name} — {formatAmount(em.saved)} av {formatAmount(em.fullAmount)} hittills, dras i {monthNameOf(em.dueMonth, em.dueYear)}
          </p>
        ))}

        {/* Expanded: transactions grouped as a tree per subcategory */}
        {isExpanded && (
          <div className="mt-3 pt-3 border-t border-gray-100" onClick={e => e.stopPropagation()}>
            {(() => {
              const items = [
                ...actuals.map(tx => ({ tx, predicted: false })),
                ...upcoming.map(tx => ({ tx, predicted: true })),
              ];
              if (items.length === 0 && (category.earmarkItems || []).length === 0) {
                return <p className="text-sm text-gray-500 text-center py-2">Inga transaktioner ännu denna månad</p>;
              }

              const renderItem = (item: { tx: Transaction; predicted: boolean }) => {
                const tx = item.tx;
                // A fully refunded purchase, or a return consumed by its
                // purchase, is settled - strike it through so it reads as
                // neutralized at a glance
                const settled = !item.predicted && (
                  (tx.amount < 0 && (tx.refundTotal || 0) >= Math.abs(tx.amount) - 0.01) ||
                  (tx.amount > 0 && !!tx.refundsTransactionId)
                );
                return (
                  <div
                    key={`${item.predicted ? 'u' : 'a'}-${tx.id}`}
                    onClick={() => { if (!item.predicted) setCategorizeTx(tx); }}
                    className={`flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 ${item.predicted ? 'opacity-70' : 'cursor-pointer hover:bg-gray-50 rounded'} ${settled ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center min-w-0">
                      <span className="text-xs text-gray-500 w-14 flex-shrink-0">
                        {!item.predicted && tx.date ? new Date(tx.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }) : ''}
                      </span>
                      <span className={`text-sm truncate ${settled ? 'line-through text-gray-400' : item.predicted ? 'text-gray-700' : 'text-gray-800'}`}>
                        {tx.description}
                      </span>
                      {item.predicted && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full flex-shrink-0">Väntas</span>
                      )}
                      {item.predicted && tx.date && (
                        <span className="ml-2 text-xs text-gray-400 flex-shrink-0 truncate">
                          brukar dras runt den {new Date(tx.date).getDate()}:{[1, 2, 21, 22, 31].includes(new Date(tx.date).getDate()) ? 'a' : 'e'}
                        </span>
                      )}
                      {!item.predicted && tx.amount > 0 && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
                          {tx.refundsTransactionId ? `Retur för ${tx.refundsTargetName || 'köp'}` : 'Inbetalning'}
                        </span>
                      )}
                      {!item.predicted && tx.amount < 0 && (tx.refundTotal || 0) > 0 && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
                          −{formatAmount(tx.refundTotal || 0)} retur
                        </span>
                      )}
                    </div>
                    <span className={`text-sm font-medium ml-3 flex-shrink-0 ${settled ? 'line-through text-gray-400' : !item.predicted && tx.amount > 0 ? 'text-green-600' : item.predicted ? 'text-gray-600' : 'text-gray-900'}`}>
                      {!item.predicted && tx.amount > 0 ? '+' : ''}
                      {formatAmount(settled && tx.amount < 0 ? Math.abs(tx.amount) : Math.abs(tx.amount) - (tx.amount < 0 ? (tx.refundTotal || 0) : 0))}
                    </span>
                  </div>
                );
              };

              // Group items per (sub)category
              const groupsById: { [id: number]: { tx: Transaction; predicted: boolean }[] } = {};
              const seenOrder: number[] = [];
              for (const item of items) {
                const gid = item.tx.category?.id ?? category.categoryId;
                if (!groupsById[gid]) { groupsById[gid] = []; seenOrder.push(gid); }
                groupsById[gid].push(item);
              }

              const childIds = (category.childBreakdown || []).map(c => c.id);
              const ordered = [
                ...childIds.filter(id => groupsById[id]),
                ...seenOrder.filter(id => !childIds.includes(id) && id !== category.categoryId),
                ...(groupsById[category.categoryId] ? [category.categoryId] : []),
              ];
              const isTree = ordered.length > 1 || (ordered.length === 1 && ordered[0] !== category.categoryId);

              const list = !isTree ? (
                <div className="space-y-1">{items.map(renderItem)}</div>
              ) : (
                <div className="space-y-3">
                  {ordered.map(gid => {
                    const group = groupsById[gid];
                    const meta = (category.childBreakdown || []).find(c => c.id === gid);
                    const isOwn = gid === category.categoryId;
                    const name = isOwn
                      ? 'Övrigt'
                      : (meta?.name || group[0].tx.category?.name || 'Övrigt');
                    // Pair-aware sum: a linked refund counts at its purchase
                    // (which shows net), never twice
                    const netSum = meta
                      ? meta.spent
                      : group.reduce((sum, item) => {
                          const t = item.tx;
                          if (item.predicted) return sum + Math.abs(t.amount);
                          if (t.amount > 0) return t.refundsTransactionId ? sum : sum - t.amount;
                          return sum + Math.max(Math.abs(t.amount) - (t.refundTotal || 0), 0);
                        }, 0);
                    const groupColor = isOwn
                      ? (category.categoryColor || '#9CA3AF')
                      : (meta?.color || group[0].tx.category?.color || '#9CA3AF');
                    return (
                      <div key={gid}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center">
                            <span className="w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: groupColor }}></span>
                            <span className="text-sm font-semibold text-gray-900">{name}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-700">{formatAmount(netSum)}</span>
                        </div>
                        <div className="ml-1 pl-3 border-l-2 border-gray-200 space-y-0.5">
                          {group.map(renderItem)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );

              return (
                <>
                  {list}
                  {(category.earmarkItems || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {category.earmarkItems.map((em, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 opacity-80">
                          <div className="flex items-center min-w-0">
                            <span className="w-14 flex-shrink-0"></span>
                            <span className="text-sm text-gray-700 truncate">{em.name}</span>
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full flex-shrink-0">Öronmärkt</span>
                          </div>
                          <div className="text-right ml-3 flex-shrink-0">
                            <span className="text-sm font-medium text-gray-600">{formatAmount(em.monthlyShare)}/mån</span>
                            <p className="text-[11px] text-gray-400">{formatAmount(em.saved)} av {formatAmount(em.fullAmount)} öronmärkt — dras i {monthNameOf(em.dueMonth, em.dueYear)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const budgetedData = budgetData.filter(cat => cat.hasBudget);
  const totalBudget = budgetedData.reduce((sum, cat) => sum + cat.budget, 0);
  const totalUpcoming = budgetedData.reduce((sum, cat) => sum + (cat.upcoming || 0), 0);
  const totalEarmarked = budgetedData.reduce((sum, cat) => sum + (cat.earmarked || 0), 0);

  // The cash verdict must count EVERY claim on the money, also in categories
  // without a monthly cap - otherwise it shows more free money than the
  // wallet view says is free. Earmarks use the accumulated amount (what
  // should be sitting untouched on the account), same figure as the wallet.
  const totalSpentAll = budgetData.reduce((sum, cat) => sum + (cat.spent || 0), 0);

  // Budget status uses the SAME spending figure the card displays. Measuring
  // "kvar" over capped categories only, while showing total spending beside
  // it, made July claim 9 893 kr left on a month that spent 41 653 kr of a
  // 36 500 kr budget. Bills still to come are only a claim on a month that
  // has not finished yet.
  const monthIsPast = currentMonth < new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const pendingClaims = monthIsPast ? 0 : totalUpcoming + totalEarmarked;
  const totalRemaining = totalBudget - totalSpentAll - pendingClaims;
  const salaryCutoff = nextSalary ? nextSalary.date : null;
  const cashUpcoming = upcomingItems
    .filter(i => i.amount < 0 && (!salaryCutoff || !i.date || i.date <= salaryCutoff))
    .reduce((sum, i) => sum + Math.abs(i.amount), 0);
  const cashEarmarked = budgetData.reduce(
    (sum, cat) => sum + (cat.earmarkItems || []).reduce((s, em) => s + (em.saved || 0), 0),
    0
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader mode="month" />
        <div className="page-content flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader mode="month" />
        <div className="page-content px-4 py-6">
          <div className="bg-white rounded-xl p-6 shadow-sm text-center">
            <AlertTriangle size={48} className="mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-semibold text-red-600 mb-2">Kunde inte ladda budget</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={loadBudgets}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Försök igen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader mode="month" />
      <div className="page-content px-4 py-6">
        {/* The calm answer: one number, one sentence. */}
        {(() => {
          const checking = bankAccounts.filter(a => a.account_type === 'CHECKING');
          const checkingSum = checking.reduce((s, a) => s + a.balance, 0);
          const disposable = checkingSum - cashUpcoming - preSalaryExpenses - cashEarmarked;
          const salaryLabel = nextSalary
            ? new Date(nextSalary.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' })
            : null;
          const showCash = isCurrentMonth() && bankAccounts.length > 0;
          const monthName = monthNameOf(currentMonth.getMonth() + 1, currentMonth.getFullYear());

          return (
            <div className="bg-white rounded-xl px-5 py-7 mb-4 shadow-sm text-center">
              {showCash ? (
                <>
                  <p className={`text-5xl font-bold mb-3 ${disposable >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                    {formatAmount(disposable)}
                  </p>
                  <p className="text-base text-gray-600">
                    {disposable >= 0
                      ? <>kan du spendera{salaryLabel ? <> fram till lönen den {salaryLabel}</> : ''}</>
                      : <>för lite{salaryLabel ? <> fram till lönen den {salaryLabel}</> : ''}</>}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-5xl font-bold text-gray-900 mb-3">{formatAmount(totalSpentAll)}</p>
                  <p className="text-base text-gray-600">gjorde du av med i {monthName}</p>
                </>
              )}

              <div className="flex items-center justify-center gap-4 mt-6">
                {isCurrentMonth() && checkupCount > 0 && (
                  <button
                    onClick={() => setDetailsOpen(true)}
                    className="text-sm font-medium text-amber-700"
                  >
                    {checkupCount} att kolla
                  </button>
                )}
                <button
                  onClick={() => setDetailsOpen(v => !v)}
                  className="inline-flex items-center text-sm font-medium text-blue-600"
                >
                  {detailsOpen ? 'Dölj detaljer' : 'Visa detaljer'}
                  {detailsOpen ? <ChevronUp size={15} className="ml-1" /> : <ChevronDown size={15} className="ml-1" />}
                </button>
              </div>
            </div>
          );
        })()}

        {detailsOpen && (<>
        {/* The monthly check-up sits above the verdict: what to DO comes
            before what things look like */}
        {isCurrentMonth() && <MonthCheckup onCount={setCheckupCount} />}
        {/* The verdict: for the current month, the money you can actually
            live on until the next salary - real balances minus the bills
            still on their way. Budget status is the secondary line. */}
        <div className="budget-summary-card bg-white rounded-xl p-5 mb-4 shadow-sm">
          {isCurrentMonth() && bankAccounts.length > 0 ? (() => {
            const checking = bankAccounts.filter(a => a.account_type === 'CHECKING');
            const checkingSum = checking.reduce((s, a) => s + a.balance, 0);
            const credit = bankAccounts.filter(a => a.account_type === 'CREDIT_CARD');
            const creditDebt = credit.reduce((s, a) => s + a.balance, 0);
            const disposable = checkingSum - cashUpcoming - preSalaryExpenses - cashEarmarked;
            const salaryLabel = nextSalary
              ? new Date(nextSalary.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' })
              : null;
            const reserved = cashUpcoming + preSalaryExpenses + cashEarmarked;
            const reservedPct = checkingSum > 0 ? Math.min((reserved / checkingSum) * 100, 100) : 100;
            return (
              <>
                {/* The number itself is already answered at the top of the
                    page - the details start with HOW it breaks down */}
                {/* Same bar grammar as the categories: the money on the
                    accounts, with the part already claimed by upcoming
                    bills striped and the free part in solid color */}
                <div
                  className="h-6 rounded-full overflow-hidden relative cursor-pointer"
                  style={{ backgroundColor: disposable >= 0 ? '#34C759' : '#FF3B30' }}
                  title="Klicka för att se plånboken - vad som dras från varje konto"
                  onClick={() => setWalletOpen(true)}
                >
                  <div
                    className="absolute top-0 left-0 h-full rounded-full"
                    style={{
                      width: `${reservedPct}%`,
                      backgroundImage: 'repeating-linear-gradient(45deg, rgba(229,231,235,0.92) 0 8px, rgba(229,231,235,0.45) 8px 12px)'
                    }}
                  ></div>
                  {reservedPct > 12 && (
                    <span className="absolute top-0 bottom-0 flex items-center text-xs font-semibold text-gray-500 pl-3">
                      {formatAmount(reserved)} {cashEarmarked > 0 ? 'intecknat' : 'dras före lönen'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {checking.map(a => `${a.account_name} ${formatAmount(a.balance)}`).join(' · ')}
                  {creditDebt > 0 && ` · kortskuld ${formatAmount(creditDebt)}`}
                  <button
                    onClick={() => setWalletOpen(true)}
                    className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Plånbok
                  </button>
                </p>
              </>
            );
          })() : (
            (() => {
              const monthName = monthNameOf(currentMonth.getMonth() + 1, currentMonth.getFullYear());
              const isPast = monthIsPast;
              const claims = pendingClaims;
              const remaining = totalRemaining;
              const uncapped = budgetData.filter(c => !c.hasBudget && (c.spent || 0) > 0);
              const uncappedSum = uncapped.reduce((s, c) => s + (c.spent || 0), 0);
              return (
                <>
                  <p className="text-sm text-gray-500 mb-1">
                    {totalBudget === 0
                      ? `Utgifter i ${monthName}`
                      : remaining >= 0
                        ? (isPast ? `Under budgeten för ${monthName}` : `Kvar av budgeten för ${monthName}`)
                        : `Över budgeten för ${monthName}`}
                  </p>
                  <p className={`text-3xl font-bold mb-2 ${totalBudget === 0 || remaining >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                    {formatAmount(totalBudget === 0 ? totalSpentAll : Math.abs(remaining))}
                  </p>
                  {claims > 0 && (
                    <p className="text-xs text-gray-500 flex items-center justify-center">
                      <Clock size={12} className="mr-1" />
                      {[
                        totalUpcoming > 0 ? `${formatAmount(totalUpcoming)} väntas dras` : null,
                        totalEarmarked > 0 ? `${formatAmount(totalEarmarked)} öronmärkt för års-/kvartalskostnader` : null,
                      ].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {/* Without this the budget looks broken: a category with no
                      monthly cap still spends money, and the user has no way
                      to see why the two numbers do not meet. */}
                  {totalBudget > 0 && uncappedSum > 0 && (
                    <p className="text-xs text-gray-500">
                      {formatAmount(uncappedSum)} av utgifterna ligger i kategorier utan månadstak
                      {uncapped.length <= 3 && ` (${uncapped.map(c => c.categoryName).join(', ')})`}
                    </p>
                  )}
                </>
              );
            })()
          )}
          {/* The month's totals. Spending counts EVERY category - a month
              without caps (older history) still had expenses. */}
          {totalBudget > 0 && (
            <div className="flex items-baseline justify-between mt-3 pt-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                Utgifter <span className={`text-lg font-bold ml-1 ${totalRemaining >= 0 ? 'text-gray-900' : 'text-red-600'}`}>{formatAmount(totalSpentAll)}</span>
              </span>
              <span className="text-sm text-gray-500">
                Budget <span className="text-lg font-bold text-gray-900 ml-1">{formatAmount(totalBudget)}</span>
              </span>
            </div>
          )}
          {actionError && (
            <p className="text-xs font-medium text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mt-2 flex items-start justify-between gap-2">
              <span>{actionError}</span>
              <button onClick={() => setActionError(null)} className="text-red-500 hover:text-red-700 flex-shrink-0"><X size={13} /></button>
            </p>
          )}
          {/* Bank consent runs out after 90 days. Without a warning the app
              just quietly stops getting new transactions. */}
          {(() => {
            const consent = bankAccounts.find(a => a.consent_status);
            if (!consent) return null;
            const expired = consent.consent_status === 'EXPIRED';
            const daysLeft = consent.consent_expires
              ? Math.ceil((new Date(consent.consent_expires).getTime() - Date.now()) / (24 * 3600 * 1000))
              : null;
            if (!expired && (daysLeft === null || daysLeft > 14)) return null;
            return (
              <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-2">
                {expired
                  ? 'Bankkopplingen har gått ut — inga nya transaktioner hämtas.'
                  : `Bankkopplingen går ut om ${daysLeft} dagar.`}{' '}
                <button onClick={() => navigate('/settings/bank-integration')} className="underline font-semibold">
                  Förnya
                </button>
              </p>
            );
          })()}
          {bankAccounts.length > 0 && (() => {
            const lastSync = bankAccounts.map(a => a.last_sync_at).filter(Boolean).sort().pop();
            const syncLabel = lastSync
              ? new Date(lastSync).toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
              : null;
            return (
              <p className="text-xs text-gray-400 mt-1 flex items-center">
                {syncLabel && <>synkat mot banken {syncLabel}</>}
                <button
                  onClick={handleSyncNow}
                  disabled={syncing}
                  className="ml-2 inline-flex items-center text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                  title="Hämta färska siffror från banken"
                >
                  <RefreshCw size={11} className={`mr-0.5 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'synkar…' : 'uppdatera'}
                </button>
              </p>
            );
          })()}
        </div>

        <div className="budget-categories-list">
          {budgetData.length > 0 ? (
            // Goal-backed categories (kassor) are rendered in the Sparmål
            // section below - they have no monthly cap to show here
            budgetData.filter(c => !goals.some(g => g.categoryId === c.categoryId)).map(renderBudgetBar)
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Inga budgetar för denna månad</h3>
              <p className="text-gray-600">Sätt tak på kategorierna för att följa dina utgifter</p>
            </div>
          )}
        </div>

        {/* Sparmål: categories measured against a saved-up kassa instead of
            a monthly cap. The bar is the kassa - spent share striped, what
            remains solid. */}
        {goals.length > 0 && (
          <div className="mt-5">
            <h2 className="text-sm font-semibold text-gray-500 mb-2 px-1">Sparmål</h2>
            {goals.map(g => {
              const goalIconMap: { [key: string]: any } = {
                umbrella: Umbrella, gift: Gift, star: Star, flag: Flag, sun: Sun,
                send: Send, camera: Camera, compass: Compass, home: Home,
                heart: Heart, truck: Truck, target: Target
              };
              const GoalIcon = goalIconMap[g.icon] || Target;
              const spentPct = g.built > 0 ? Math.min((g.spent / g.built) * 100, 100) : 0;
              // A pot is measured against its target, not against deposits
              const isPot = g.kind === 'pot';
              const targetPct = g.targetAmount > 0 ? Math.min((g.kassa / g.targetAmount) * 100, 100) : 0;
              if (isPot) {
                return (
                  <div key={g.id} className="bg-white rounded-xl p-4 mb-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-flex items-center text-base font-semibold text-gray-900">
                        <GoalIcon size={17} className="mr-2 flex-shrink-0" style={{ color: g.color || '#0EA5E9' }} />
                        {g.name}
                      </span>
                      <span className="text-base font-bold text-gray-900">{formatAmount(g.kassa)}</span>
                    </div>
                    {g.targetAmount > 0 ? (
                      <>
                        <div className="h-5 rounded-full overflow-hidden bg-gray-100">
                          <div className="h-full rounded-full" style={{ width: `${targetPct}%`, backgroundColor: g.missingToTarget > 0 ? '#F59E0B' : '#34C759' }}></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5">
                          {g.missingToTarget > 0
                            ? `${formatAmount(g.missingToTarget)} kvar till målet ${formatAmount(g.targetAmount)}`
                            : `Målet ${formatAmount(g.targetAmount)} är nått`}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-500">Sätt en målsumma under Inställningar → Sparmål.</p>
                    )}
                  </div>
                );
              }
              return (
                <div key={g.id} className="bg-white rounded-xl p-4 mb-3 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center text-base font-semibold text-gray-900">
                      <GoalIcon size={17} className="mr-2 flex-shrink-0" style={{ color: g.color || '#0EA5E9' }} />
                      {g.name}
                    </span>
                    <span className={`text-base font-bold ${g.kassa > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {formatAmount(g.kassa)} i kassan
                    </span>
                  </div>
                  {g.built > 0 ? (
                    <>
                      <div className="h-5 rounded-full overflow-hidden relative" style={{ backgroundColor: '#34C759' }}>
                        <div
                          className="absolute top-0 left-0 h-full"
                          style={{
                            width: `${spentPct}%`,
                            backgroundImage: 'repeating-linear-gradient(45deg, rgba(229,231,235,0.92) 0 8px, rgba(229,231,235,0.45) 8px 12px)'
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5">
                        {formatAmount(g.spent)} använt av {formatAmount(g.built)} insatt
                        {g.monthlyTarget ? ` · fylls på ${formatAmount(g.monthlyTarget)}/mån` : ''}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="h-5 rounded-full bg-gray-100"></div>
                      <p className="text-xs text-gray-500 mt-1.5">
                        Kassan är tom än{g.monthlyTarget ? ` — tänkt påfyllning ${formatAmount(g.monthlyTarget)}/mån` : ''}.
                        Köp som kategoriseras som {g.categoryName} räknas mot kassan istället för ett månadstak.
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>)}
      </div>

      {categorizeTx && (
        <CategorizeModal
          tx={categorizeTx}
          onClose={() => setCategorizeTx(null)}
          onSaved={async () => {
            setCategorizeTx(null);
            await Promise.all([loadBudgets(), loadTransactions()]);
          }}
          onDataChanged={() => { loadBudgets(); loadTransactions(); }}
        />
      )}
      {walletOpen && (
        <WalletModal
          accounts={bankAccounts}
          items={[
            ...upcomingItems.filter(i => i.amount < 0 && (!salaryCutoff || !i.date || i.date <= salaryCutoff)),
            ...preSalaryItems.filter(i => i.amount < 0),
          ].map((i: any) => ({
            id: String(i.id),
            description: i.description,
            amount: i.amount,
            date: i.date,
            accountId: i.accountId ?? null,
            accountName: i.accountName ?? null,
          }))}
          earmarks={budgetData.flatMap(b => b.earmarkItems)}
          salaryLabel={nextSalary
            ? new Date(nextSalary.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' })
            : null}
          onClose={() => setWalletOpen(false)}
        />
      )}
    </div>
  );
};

export default BudgetPage;
